import * as React from 'react';
import { Button, Input, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { errorText, useApp } from '../app/context';
import { siteUrlOf } from '../app/App';
import { takeResume } from '../auth';
import { setUpRestricted, SharePointSite } from '../records/restrictedSetup';
import { RESTRICTED_GROUP, RESTRICTED_LIBRARY } from '../records/schemas';

/**
 * Restricted files: refugee and private-sponsorship files (and any the firm marks) kept in their
 * own library and lists, open only to a group of the site. Set up once by a site owner.
 */
export default function RestrictedCard(): React.ReactElement | null {
  const app = useApp();
  const w = app.workspace;
  const siteUrl: string = siteUrlOf(w.webUrl);
  const site = React.useMemo(() => new SharePointSite(siteUrl), [siteUrl]);
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [members, setMembers] = React.useState<{ id: number; name: string; email: string }[] | undefined>(undefined);
  const [groupId, setGroupId] = React.useState<number | undefined>(undefined);
  const [email, setEmail] = React.useState<string>('');
  const ready: boolean = !!w.firm?.restricted;

  const setUp = React.useCallback(async (): Promise<void> => {
    setError(undefined);
    try {
      setWorking('Asking Microsoft for permission to manage this site…');
      await site.ask({ kind: 'restrictedSetUp' });
      const r = await setUpRestricted(app.graph, app.manageGraph, w, siteUrl, app.account.username, setWorking);
      await app.saveWorkspace({ ...w, firm: { ...w.firm!, restricted: { ...r, ready: new Date().toISOString() } } });
      app.toast({ text: `Restricted files are set up. Only the "${RESTRICTED_GROUP}" group and the site's owners can open them.` });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(undefined);
    }
  }, [app, w, site, siteUrl]);

  React.useEffect(() => {
    if (takeResume('restrictedSetUp')) {
      void setUp();
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMembers = async (): Promise<void> => {
    setError(undefined);
    try {
      const id: number | undefined = await site.group();
      setGroupId(id);
      setMembers(id === undefined ? [] : await site.members(id));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const change = async (step: () => Promise<void>): Promise<void> => {
    setError(undefined);
    try {
      await step();
      await loadMembers();
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (!w.firm) {
    return null;
  }
  return (
    <section className="card" aria-labelledby="restricted-h" style={{ gap: 10 }}>
      <h2 id="restricted-h">Restricted files</h2>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {!ready && (
        <>
          <span className="small" style={{ fontSize: 14 }}>
            Refugee and private-sponsorship files (and any file you mark) can be kept in their own library ("{RESTRICTED_LIBRARY}"), lists and history,
            open only to a "{RESTRICTED_GROUP}" group and the site's owners. Only a site owner can set this up. You'll be the group's first member.
            Microsoft will ask you to allow this app to manage the site's permissions, and only here, not at sign-in. If it says an administrator must approve, ask your Microsoft 365 administrator to approve "AllSites.FullControl" for this app, then try again.
          </span>
          {working
            ? <Spinner size="small" label={working} labelPosition="after" />
            : <Button appearance="primary" onClick={() => { void setUp(); }} style={{ alignSelf: 'flex-start' }}>Set up restricted files</Button>}
          <details>
            <summary className="small">If setup can't finish</summary>
            <span className="small">A site owner can do the same by hand in SharePoint: create a group "{RESTRICTED_GROUP}", then in each of the "{RESTRICTED_LIBRARY}" library and the lists starting with "Restricted:", stop inheriting permissions and give only that group (Edit) and the site's owners (Full Control) access. Then press "Set up restricted files" again.</span>
          </details>
        </>
      )}
      {ready && (
        <>
          <span className="small" style={{ fontSize: 14 }}>Set up. Restricted files are in "{w.firm.restricted!.libraryName}" and the lists starting with "Restricted:"; only the "{RESTRICTED_GROUP}" group and the site's owners can open them, and their changes are logged in their own history.</span>
          {!members && <Button onClick={() => { void loadMembers(); }} style={{ alignSelf: 'flex-start' }}>Show who's in the group</Button>}
          {members && (
            <>
              <ul className="steps-list" style={{ fontSize: 14 }}>
                {members.map(m => (
                  <li key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {m.name}{m.email ? ` (${m.email})` : ''}
                    <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={`Remove ${m.name}`} onClick={() => { void change(() => site.removeMember(groupId!, m.id)); }} />
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: 8, maxWidth: 480 }}>
                <Input placeholder="name@yourfirm.ca" value={email} onChange={(_, d) => setEmail(d.value)} style={{ flexGrow: 1 }} />
                <Button disabled={!/^[^@\s]+@[^@\s]+$/.test(email) || groupId === undefined} onClick={() => { void change(() => site.addMember(groupId!, email.trim())).then(() => setEmail('')); }}>Add</Button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
